/**
 * Provides a component for the min/max range inputs.
 *
 * @packageDocumentation
 * @module MinMaxRangeInputs
 * @internal
 */

import { Box, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

// -- type definitions --

/**
 * Props for the MinMaxRangeInputs component.
 */
interface MinMaxRangeInputsProps {
  min: number | undefined;
  max: number | undefined;
  minLabel: string;
  maxLabel: string;
  helperText?: string;
  onChangeMin: (value: number | undefined) => void;
  onChangeMax: (value: number | undefined) => void;
  disabled: boolean;
}

// -- component --

/**
 * The MinMaxRangeInputs component used to set the min/max range for an axis.
 */
export const MinMaxRangeInputs: React.FC<MinMaxRangeInputsProps> = ({
  min,
  max,
  minLabel,
  maxLabel,
  helperText,
  onChangeMin,
  onChangeMax,
  disabled,
}) => {
  const [minInput, setMinInput] = useState(
    min !== undefined ? String(min) : ''
  );
  const [maxInput, setMaxInput] = useState(
    max !== undefined ? String(max) : ''
  );

  useEffect(() => {
    setMinInput(min !== undefined ? String(min) : '');
  }, [min]);

  useEffect(() => {
    setMaxInput(max !== undefined ? String(max) : '');
  }, [max]);

  const commitMin = () => {
    const trimmed = minInput.trim();
    if (trimmed === '') {
      onChangeMin(undefined);
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isNaN(parsed)) {
      onChangeMin(parsed);
    }
  };

  const commitMax = () => {
    const trimmed = maxInput.trim();
    if (trimmed === '') {
      onChangeMax(undefined);
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isNaN(parsed)) {
      onChangeMax(parsed);
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      {helperText ? (
        <Typography variant="caption" color="text.secondary">
          {helperText}
        </Typography>
      ) : null}
      <Box
        display="grid"
        gridTemplateColumns="repeat(2, minmax(0, 1fr))"
        gap={1}
      >
        <TextField
          label={minLabel}
          type="number"
          value={minInput}
          onChange={(e) => setMinInput(e.target.value)}
          onBlur={commitMin}
          disabled={disabled}
          size="small"
          fullWidth
        />
        <TextField
          label={maxLabel}
          type="number"
          value={maxInput}
          onChange={(e) => setMaxInput(e.target.value)}
          onBlur={commitMax}
          disabled={disabled}
          size="small"
          fullWidth
        />
      </Box>
    </Box>
  );
};
